import { useState } from 'react';
import { Briefcase, ChevronDown, Radio, Shield, ShoppingBag, Wrench } from 'lucide-react';
import { formatMXN } from '@/lib/api';
import { formatearFechaCorta } from '@/lib/pedidos';
import { Panel } from '@/components/ui';
import { cn } from '@/lib/utils';
import type { HistorialAlarcom, HistorialAlarcomItem } from '@/types';

type TabId = 'compras' | 'servicios' | 'monitoreos' | 'rma';

function claseEstatus(estatus: string): string {
  const e = estatus.toLowerCase();
  if (e.includes('complet') || e === 'activo' || e === 'cerrado') {
    return 'bg-emerald-100 text-emerald-800';
  }
  if (e.includes('cancel') || e === 'inactivo') {
    return 'bg-surface-100 text-surface-600';
  }
  if (e.includes('proceso') || e.includes('program') || e.includes('pendiente')) {
    return 'bg-amber-100 text-amber-800';
  }
  return 'bg-surface-100 text-surface-700';
}

function Badge({ children }: { children: string }) {
  return (
    <span className={`inline-flex rounded-full px-2 py-0.5 text-[11px] font-medium ${claseEstatus(children)}`}>
      {children}
    </span>
  );
}

export function HistorialAlarcomPanel({ historial }: { historial: HistorialAlarcom | null }) {
  const [tab, setTab] = useState<TabId | null>(null);

  if (!historial) return null;

  const { resumen, compras, monitoreos, servicios } = historial;
  const monitoreosTotal = resumen.monitoreos_activos + resumen.monitoreos_inactivos;
  const vacio =
    resumen.compras === 0 &&
    resumen.servicios === 0 &&
    monitoreosTotal === 0 &&
    resumen.rma === 0;

  const tabs: Array<{
    id: TabId;
    label: string;
    valor: number;
    icon: typeof ShoppingBag;
  }> = [
    { id: 'compras', label: 'Compras', valor: resumen.compras, icon: ShoppingBag },
    { id: 'servicios', label: 'Servicios', valor: resumen.servicios, icon: Wrench },
    { id: 'monitoreos', label: 'Monitoreos', valor: monitoreosTotal, icon: Radio },
    { id: 'rma', label: 'RMA', valor: resumen.rma, icon: Shield },
  ];

  const toggle = (id: TabId) => {
    setTab((actual) => (actual === id ? null : id));
  };

  return (
    <Panel
      title="Con tu vendedor"
      icon={<Briefcase className="h-4 w-4 text-brand-600" />}
      className="lg:col-span-2"
    >
      <p className="mb-4 text-sm text-surface-500">
        Historial que tu ejecutivo registró en Alarcom. Elige una categoría para ver el detalle.
      </p>

      {vacio ? (
        <p className="text-sm text-surface-500">
          Aún no hay compras ni servicios registrados por tu vendedor.
        </p>
      ) : (
        <>
          <div className="grid grid-cols-2 gap-2 sm:grid-cols-4" role="tablist" aria-label="Historial Alarcom">
            {tabs.map((item) => {
              const Icon = item.icon;
              const activo = tab === item.id;
              return (
                <button
                  key={item.id}
                  type="button"
                  role="tab"
                  aria-selected={activo}
                  aria-controls={`historial-panel-${item.id}`}
                  id={`historial-tab-${item.id}`}
                  onClick={() => toggle(item.id)}
                  className={cn(
                    'flex items-center gap-3 rounded-lg border px-3 py-3 text-left transition-colors',
                    activo
                      ? 'border-brand-600 bg-brand-50'
                      : 'border-surface-200 bg-white hover:border-brand-300 hover:bg-surface-50',
                  )}
                >
                  <Icon className={cn('h-4 w-4 shrink-0', activo ? 'text-brand-700' : 'text-surface-400')} />
                  <span className="min-w-0">
                    <span className="block font-display text-lg font-bold tabular-nums leading-none text-surface-900">
                      {item.valor}
                    </span>
                    <span className="mt-1 block text-[11px] leading-tight text-surface-500">{item.label}</span>
                  </span>
                  <ChevronDown
                    className={cn(
                      'ml-auto h-4 w-4 shrink-0 text-surface-400 transition-transform',
                      activo && 'rotate-180 text-brand-700',
                    )}
                  />
                </button>
              );
            })}
          </div>

          {tab ? (
            <div
              id={`historial-panel-${tab}`}
              role="tabpanel"
              aria-labelledby={`historial-tab-${tab}`}
              className="mt-4"
            >
              {tab === 'compras' &&
                (compras.length === 0 ? (
                  <TablaVacia texto="Sin compras fuera de la tienda." />
                ) : (
                  <ListaCompras items={compras} />
                ))}
              {tab === 'servicios' &&
                (servicios.length === 0 ? (
                  <TablaVacia texto="Sin visitas de servicio." />
                ) : (
                  <ListaSimple
                    items={servicios}
                    primario={(i) => i.tipo || 'Servicio técnico'}
                    secundario={(i) => formatearFechaCorta(i.fecha)}
                  />
                ))}
              {tab === 'monitoreos' && (
                <>
                  <p className="mb-2 text-xs text-surface-500">
                    {resumen.monitoreos_activos} activos · {resumen.monitoreos_inactivos} inactivos
                  </p>
                  {monitoreos.length === 0 ? (
                    <TablaVacia texto="Sin cuentas de monitoreo." />
                  ) : (
                    <ListaSimple
                      items={monitoreos}
                      primario={(i) => i.plan || 'Monitoreo'}
                      secundario={(i) =>
                        i.fecha
                          ? `Instalado ${formatearFechaCorta(i.fecha)}`
                          : 'Sin fecha de instalación'
                      }
                    />
                  )}
                </>
              )}
              {tab === 'rma' && (
                <p className="rounded-lg border border-surface-200 bg-surface-50 px-3 py-3 text-sm text-surface-600">
                  {resumen.rma === 0
                    ? 'No hay RMA registrados.'
                    : `Tienes ${resumen.rma} RMA. El seguimiento lo hace tu vendedor en Alarcom.`}
                </p>
              )}
            </div>
          ) : (
            <p className="mt-3 text-xs text-surface-400">Toca una categoría para ver el detalle.</p>
          )}
        </>
      )}
    </Panel>
  );
}

function TablaVacia({ texto }: { texto: string }) {
  return <p className="px-1 py-3 text-sm text-surface-500">{texto}</p>;
}

function ListaCompras({ items }: { items: HistorialAlarcomItem[] }) {
  return (
    <ul className="divide-y divide-surface-100 overflow-hidden rounded-lg border border-surface-200">
      {items.map((item, idx) => (
        <li key={item.folio ?? idx} className="flex flex-wrap items-center justify-between gap-2 px-3 py-2.5">
          <div>
            <p className="text-sm font-medium text-surface-800">
              {item.tipo || 'Venta'}
              {item.folio ? (
                <span className="ml-2 font-mono text-xs font-normal text-surface-400">#{item.folio}</span>
              ) : null}
            </p>
            <p className="text-xs text-surface-500">{formatearFechaCorta(item.fecha)}</p>
          </div>
          <div className="flex items-center gap-3">
            <Badge>{item.estatus}</Badge>
            {typeof item.total === 'number' ? (
              <span className="text-sm font-semibold tabular-nums text-surface-900">
                {formatMXN(item.total)}
              </span>
            ) : null}
          </div>
        </li>
      ))}
    </ul>
  );
}

function ListaSimple({
  items,
  primario,
  secundario,
}: {
  items: HistorialAlarcomItem[];
  primario: (item: HistorialAlarcomItem) => string;
  secundario: (item: HistorialAlarcomItem) => string;
}) {
  return (
    <ul className="divide-y divide-surface-100 overflow-hidden rounded-lg border border-surface-200">
      {items.map((item, idx) => (
        <li
          key={item.folio ?? `${primario(item)}-${idx}`}
          className="flex items-center justify-between gap-3 px-3 py-2.5"
        >
          <div>
            <p className="text-sm font-medium text-surface-800">{primario(item)}</p>
            <p className="text-xs text-surface-500">{secundario(item)}</p>
          </div>
          <Badge>{item.estatus}</Badge>
        </li>
      ))}
    </ul>
  );
}
